import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { ValidationMethod } from '../../../../types/ValidatePhaseTypes';
import { HbrIcon, HbrOption, HbrSelect, HbrSpinner } from '@harbor/elements-react-standalone';
import { OpportunityProduct } from '../../../../types/opportunity';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../support/store';
import { useFetchOpportunity, useFetchOpportunityProductAssignments, useUpdateOpportunityProductAssignmentMutation } from '../../../../hooks/opportunity-hooks';
import { CacheKey } from '../../../../types/cache-keys';
import { statusWarning } from '@harbor/elements/icons';
import { useParams } from 'react-router-dom';
import StbButton from '../../../security-toolbox-ui/StbButton';
import { asSingleValue } from '../../../../support/utils';
import { isPovStarted } from '../../opportunityDetailHelpers';
import { useCreatePoVActivity } from '../../../../hooks/opportunity-pov-hooks';


const ValidationTypeSelection = [
  {label: 'Validated', value: ValidationMethod.Validated },
  {label: 'Needs Validation', value: ValidationMethod.NeedValidation },
  {label: 'PoV', value: ValidationMethod.PoV}
]

const ValidatePhaseQualification: React.FC<{
  setPovProducts: React.Dispatch<React.SetStateAction<OpportunityProduct[]>>;
  handlePovProductSelect: (productId: string) => void;
}> = ({
  setPovProducts,
  handlePovProductSelect,
}) => {
  const { id: opportunityId } = useParams();
  const queryClient = useQueryClient();

  const { data: opportunity } = useFetchOpportunity(opportunityId);

  const [opportunityProducts, setOpportunityProducts] = useState<OpportunityProduct[]>([]);
  const [isSavingOpportunityProduct, setIsSavingOpportunityProduct] = useState<number>(null);

  const { data: products, isLoading: isLoadingProducts } = useFetchOpportunityProductAssignments({ opportunity: opportunity?.id ?? opportunity?.Id }, { enabled: !!(opportunity.id || opportunity?.Id) });

  // Retry PoV activity creation for products that had the task fail on first attempt
  const createPoVActivity = useCreatePoVActivity(opportunityId);
  const { mutateAsync: updateOpportunityProduct, isPending: isUpdatingOpportunityProduct } = useUpdateOpportunityProductAssignmentMutation();
  
  const processedProductIds = React.useRef(new Set<number>());
  useEffect(() => {
    if (isLoadingProducts || !products || products?.length === 0) return;
    products
      .filter((product) =>
        isPovStarted(product) && !product?.sf_pov_activity_created && !processedProductIds.current.has(product.id)
      )
      .map((product) => {
        processedProductIds.current.add(product.id);
        createPoVActivity.mutate(product.id, {
          onError: (error) => {
            console.error(`Failed to create PoV activity for ${product?.name}:`, error);
          },
          onSuccess: async (_response) => {
            updateOpportunityProduct({
              ...product,
              product_id: product.product.id,
              product: product.product.id,
              ...(_response && !_response.error && {
                sf_pov_activity_id: _response.taskResponse?.id,
                sf_pov_activity_created: _response.taskResponse?.success,
              }),
            });
          }
        });
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, isLoadingProducts, updateOpportunityProduct]);

  const allProducts = useSelector((state: RootState) => state.products.items);

  const { mutateAsync: updateProduct } = useUpdateOpportunityProductAssignmentMutation();

  useEffect(() => {
    if ( products?.length > 0) {
      const ciscoProducts = products
        .filter((product) => product?.product?.vendor?.is_cisco)
        .sort((a, b) => a.product.name.localeCompare(b.product.name));
      setOpportunityProducts(ciscoProducts);
      setPovProducts(ciscoProducts
        .filter((product) => [ValidationMethod.NeedValidation, ValidationMethod.PoV].includes(product?.validation_method as ValidationMethod))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, allProducts]);

  const handleValidationMethodSelect = async (method: ValidationMethod, product: OpportunityProduct) => {
    setIsSavingOpportunityProduct(product.id);
    try {
      const updated: OpportunityProduct = await updateProduct(
        { ...product, validation_method: method, product_id: product.product.id },
        {
          onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: [CacheKey.OpportunityProductAssignment, variables.opportunity] });
            queryClient.invalidateQueries({ queryKey: [CacheKey.OpportunityProduct, variables.opportunity] });
          }
        }
      );
      const products = opportunityProducts.filter((opProduct) => updated.id !== opProduct.id);
      setOpportunityProducts([...products, updated].sort((a, b) => (a.product.name ?? '').localeCompare(b.product.name ?? '')));
      setPovProducts([...products, updated]
        .filter((product) => [ValidationMethod.NeedValidation, ValidationMethod.PoV].includes(product.validation_method as ValidationMethod))
        .sort((a, b) => a.product.name.localeCompare(b.product.name))
      );
    } catch (error) {
      console.error('Error saving outcome:', error);
    } finally {
      setIsSavingOpportunityProduct(null);
    }
  }

  const handleOpportunityProductClick = (product: OpportunityProduct) => {
    handlePovProductSelect(product.product.name);
  }

  return (
    <>
      {isLoadingProducts && <div className="flex flex-row w-full gap-4 justify-between align-center mb-2"><HbrSpinner  size="large">
        <div slot="label">Loading...</div></HbrSpinner></div>
      }
      {!(products && products.length > 0) && !isLoadingProducts && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md mb-4 flex items-center gap-2">
          <span className="text-yellow-500"><HbrIcon asset={statusWarning} sentiment={"warning"} /></span>
          Please add Opportunity Solution products
        </div>
      )}
      {opportunityProducts?.map((product) => (
        <div key={product.id} className="grid grid-cols-[1fr_192px] mb-2">
          <StbButton
            onClick={() => handleOpportunityProductClick(product)}
            loading={product.id === isSavingOpportunityProduct}
            pill={false}
            variant="fill"
            size="medium"
            disabled={![ValidationMethod.NeedValidation, ValidationMethod.PoV].includes(product?.validation_method as ValidationMethod)}
          >
            {product.product.name}
          </StbButton>
          <HbrSelect
            placeholder='Select Validation Option'
            value={product?.validation_method}
            onHbr-change={(e) =>
              handleValidationMethodSelect(
                asSingleValue(e.target.value) as ValidationMethod,
                product
              )
            }
          >
            {ValidationTypeSelection.map((option) => (
              <HbrOption key={option.value} value={option.value}>
                {option.label}
              </HbrOption>
            ))}
          </HbrSelect>
        </div>
      ))}
    </>)
}
export default ValidatePhaseQualification;